import React from 'react';
import { useUploadStorefrontBannerImageMutation } from '../../../../store/Api';
import { IsMutationSuccess } from '../../../../logic/MutationTypeChecker';
import { IUploadFileResponse } from '../../../../dal/BaseDAL';
import { Icon, MessageBar, MessageBarType, Spinner, SpinnerSize } from '@fluentui/react';
import { useAssetSource } from '../../Assets/AssetSourceContext';
import AssetSourceMenu, { IAssetSourceMenuHandle } from '../../Assets/AssetSourceMenu';
import AssetPickerPanel from '../../Assets/AssetPickerPanel';
import { useUploadStorefrontAssetMutation } from '../../../../store/endpoints/StorefrontAssetEndpoints';
import { IStorefrontAssetUploadResponse } from '../../../../models/Storefront';

interface IStorefrontBannerImageUploaderProps {
    value?: string;
    onChange: (value: string) => void;
    width?: number;
    height?: number;
    imageId?: string;
    isNew?: boolean;
}

const StorefrontBannerImageUploader: React.FC<IStorefrontBannerImageUploaderProps> = (props) => {
    const [uploadStorefrontBannerImage] = useUploadStorefrontBannerImageMutation();
    const [uploadStorefrontAsset] = useUploadStorefrontAssetMutation();
    // Present on the storefront settings screens; absent elsewhere, where this keeps its legacy
    // upload-only behaviour.
    const assetSource = useAssetSource();
    const useAssetLibrary = !!assetSource;
    const menuRef = React.useRef<IAssetSourceMenuHandle>(null);
    const [pickerOpen, setPickerOpen] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [saveSuccess, setSaveSuccess] = React.useState(false);
    const [saveMessage, setSaveMessage] = React.useState<string | undefined>();

    const inputRef = React.useRef<HTMLInputElement>(null);

    const onButtonClick = () => {
        if (saving) return;
        // Chooser instead of a straight file dialog when the asset library is available.
        if (useAssetLibrary) {
            menuRef.current?.open();
            return;
        }
        inputRef.current?.click();
    }

    const onChangeFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        e.preventDefault();
        const file = e.target.files?.[0];
        if (!file) return;
        setSaveMessage(undefined);
        const reader = new FileReader();
        reader.onload = async () => {
            const f = reader.result;
            if (!f || typeof f === 'string') return;
            if (file.size > 20971520) {
                setSaveMessage("File size exceeds 20 MB");
                setSaveSuccess(false);
                return;
            }
            setSaving(true);

            const ext = file.name.split('.').pop() ?? 'png';
            const fileName = `${props.imageId ?? crypto.randomUUID()}.${ext}`;

            if (useAssetLibrary && assetSource) {
                // Banners keep animation (GIFs are a legitimate banner format).
                const assetResponse = await uploadStorefrontAsset({
                    file: f,
                    fileName,
                    storefront: assetSource.storefrontId,
                    name: file.name.replace(/\.[^./\\]+$/, ''),
                    allowAnimated: true,
                });
                const assetData = (assetResponse as { data?: IStorefrontAssetUploadResponse }).data;
                const assetUrl = assetData?.asset?.url ?? assetData?.url;
                if (assetUrl) {
                    setSaveMessage('Image uploaded successfully');
                    setSaveSuccess(true);
                    props.onChange(assetUrl);
                } else {
                    console.error(['FAILED UPLOAD', assetResponse]);
                    setSaveMessage('Failed to upload image');
                    setSaveSuccess(false);
                }
                setSaving(false);
                return;
            }

            const response = await uploadStorefrontBannerImage({ file: f, fileName });
            if (IsMutationSuccess<IUploadFileResponse>(response) && response.data?.url) {
                setSaveMessage('Image uploaded successfully');
                setSaveSuccess(true);
                props.onChange(response.data.url);
            } else {
                console.error(['FAILED UPLOAD', response]);
                setSaveMessage('Failed to upload image');
                setSaveSuccess(false);
            }
            setSaving(false);
        };
        reader.readAsArrayBuffer(file);
    }

    return (
        <div onClick={onButtonClick} style={{width: props.width, height: props.height, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: saving ? 'default' : 'pointer', position: 'relative', overflow: 'hidden'}}>
            <input
                type='file'
                ref={inputRef}
                style={{ display: 'none' }}
                onChange={onChangeFile}
                accept='.gif,.png,.jpg,.jpeg,.webp,.svg,image/svg+xml'
            />
            {props.value && (
                <img
                    src={props.value}
                    alt='Banner'
                    style={{ width: props.width, height: props.height }}
                />
            )}
            {!props.value && !saving && props.isNew && (
                <Icon iconName='Add' />
            )}
            {useAssetLibrary && !saving && (
                // Chevron affordance overlaid on the tile — the tile itself keeps its own click.
                <AssetSourceMenu
                    ref={menuRef}
                    onSelectAsset={() => setPickerOpen(true)}
                    onUploadNew={() => inputRef.current?.click()}
                />
            )}
            {useAssetLibrary && assetSource && (
                <AssetPickerPanel
                    isOpen={pickerOpen}
                    onDismiss={() => setPickerOpen(false)}
                    storefrontId={assetSource.storefrontId}
                    onSelect={(asset) => {
                        setPickerOpen(false);
                        props.onChange(asset.url);
                    }}
                />
            )}
            {saving && (
                <div style={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.75)',
                }}>
                    <Spinner size={SpinnerSize.large} />
                </div>
            )}
            {!saving && !saveSuccess && saveMessage && (
                // Overlay the tile (which is fixed-size with overflow hidden) rather than
                // flowing below it. Stop clicks so dismissing doesn't reopen the file picker.
                <div
                    style={{ position: 'absolute', left: 0, right: 0, bottom: 0 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        onDismiss={() => setSaveMessage(undefined)}
                        dismissButtonAriaLabel='Dismiss'
                    >
                        {saveMessage}
                    </MessageBar>
                </div>
            )}
        </div>
    );
}

export default StorefrontBannerImageUploader;